import { memo, useContext, useState } from "react";
import PropTypes from "prop-types";
import "./SmartDPMapMarker.scss";
import classNames from "classnames";
import { Amount } from "app/components/ui/Amount/Amount";
import IconLock from "app/pages/.shared/IconLock";
import IconLightning from "app/pages/.shared/IconLightning";
import { PRODUCT_TAG_CODES } from "app/constants";
import { SquareLockFilledIcon, FireFilledIcon, HeartFilledIcon } from "app/icons/navigation";
import { TagFilledIcon } from "app/icons/finance";
import { BaggageLFilledIcon } from "app/icons/travel";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS } from "app/constants";

const getSmartDPMapMarkerIcon = tag =>
	({
		[PRODUCT_TAG_CODES.GC_DISCONNECTED]: SquareLockFilledIcon,
		[PRODUCT_TAG_CODES.LIMITED_TIME]: FireFilledIcon,
		[PRODUCT_TAG_CODES.LAST_DAY]: FireFilledIcon,
		[PRODUCT_TAG_CODES.COUP_DE_COEUR]: HeartFilledIcon,
		[PRODUCT_TAG_CODES.DISCOUNT]: TagFilledIcon,
		[PRODUCT_TAG_CODES.BAGGAGE_INCLUDED]: BaggageLFilledIcon,
	})[tag] || null;

export const SmartDPMapMarker = ({
	name,
	productUrl,
	total,
	isActive,
	onMarkerClick,
	isFlashsale,
	amountSuffix,
	tag,
	shape = "full",
}) => {
	const { brand } = useContext(AppGlobalsContext);
	const [isHovered, setIsHovered] = useState(false);
	const isBrandAF = brand === BRANDS.AF;
	const isBrandTO = brand === BRANDS.TO;
	const isBrandEK = brand === BRANDS.EK;
	// A mini marker collapses to a small white pill until it is hovered or selected,
	// at which point it expands back to the full price vignette (with shadow).
	const displayAsMini = shape === "mini" && !isHovered && !isActive;
	const smartDPMapMarkerClasses = classNames({
		"smartDPMap-marker": true,
		"smartDPMap-marker--full": !displayAsMini,
		"smartDPMap-marker--mini": displayAsMini,
		"smartDPMap-marker--expanded": shape === "mini" && !displayAsMini,
		"smartDPMap-marker--hover": isActive,
		"smartDPMap-marker--flashsale": isFlashsale,
		"smartDPMap-marker--has-tag": !!tag,
		"smartDPMap-marker--GC_DISCONNECTED": tag === PRODUCT_TAG_CODES.GC_DISCONNECTED,
		"smartDPMap-marker--GC_DISCONNECTED-af":
			tag === PRODUCT_TAG_CODES.GC_DISCONNECTED && isBrandAF,
		"smartDPMap-marker--GC_DISCONNECTED-to":
			tag === PRODUCT_TAG_CODES.GC_DISCONNECTED && isBrandTO,
		"smartDPMap-marker--GC_DISCONNECTED-ek":
			tag === PRODUCT_TAG_CODES.GC_DISCONNECTED && isBrandEK,
		"smartDPMap-marker--LIMITED_TIME": tag === PRODUCT_TAG_CODES.LIMITED_TIME,
		"smartDPMap-marker--LAST_DAY": tag === PRODUCT_TAG_CODES.LAST_DAY,
		"smartDPMap-marker--CDC": tag === PRODUCT_TAG_CODES.COUP_DE_COEUR,
		"smartDPMap-marker--DISCOUNT": tag === PRODUCT_TAG_CODES.DISCOUNT,
		"smartDPMap-marker--BAGGAGE_INCLUDED": tag === PRODUCT_TAG_CODES.BAGGAGE_INCLUDED,
	});

	const TagIcon = getSmartDPMapMarkerIcon(tag);
	const tagIconElement = TagIcon ? <TagIcon /> : null;

	let markerElement = (
		<div className="smartDPMap-marker__content">
			{tagIconElement}
			<Amount value={total} suffix={amountSuffix} className="smartDPMap-marker__amount" />
		</div>
	);

	if (isFlashsale) {
		markerElement = (
			<div className="smartDPMap-marker__content">
				{tagIconElement}
				<IconLightning />
				<Amount value={total} suffix={amountSuffix} className="smartDPMap-marker__amount" />
			</div>
		);
	} else if (productUrl) {
		markerElement = (
			<div className="smartDPMap-marker__content">
				{tagIconElement}
				<IconLock />
			</div>
		);
	}

	if (displayAsMini) {
		markerElement = <div className="smartDPMap-marker__content" />;
	}

	return (
		<div
			data-testid={`smartdp-map-marker-${name}`}
			onClick={e => {
				e.stopPropagation();
				onMarkerClick(name);
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			className={smartDPMapMarkerClasses}
			style={{
				zIndex: isActive || isHovered || !displayAsMini ? 998 : 1,
			}}
		>
			{markerElement}
		</div>
	);
};

SmartDPMapMarker.propTypes = {
	isFlashsale: PropTypes.bool,
	isActive: PropTypes.bool,
	productUrl: PropTypes.string,
	amountSuffix: PropTypes.string,
	total: PropTypes.number,
	name: PropTypes.string,
	onMarkerClick: PropTypes.func,
	tag: PropTypes.oneOf(Object.values(PRODUCT_TAG_CODES)),
	shape: PropTypes.oneOf(["full", "mini"]),
};

export default memo(SmartDPMapMarker);
